import { State, Action, StateContext, Selector } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { GetAccessToken, GetAccessTokenSuccess, GetAccessTokenFailure } from './auth.actions';
import { tap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { LocalStorageService } from '../../core/services/local-storage.service';
import { LocalStorageKeys } from '../../core/constants/local-storage-keys.enum';

export interface AuthStateModel {
  accessToken: string | null;
  refreshToken: string | null;
  sellerName: string | null;
  grantedScopes: string[];
  error: any;
}

@State<AuthStateModel>({
  name: 'auth',
  defaults: {
    accessToken: null,
    refreshToken: null,
    sellerName: null,
    grantedScopes: [],
    error: null
  }
})
@Injectable()
export class AuthState {
  constructor(
    private authService: AuthService,
    private localStorage: LocalStorageService
  ) {}

  @Selector()
  static accessToken(state: AuthStateModel): string | null {
    return state.accessToken;
  }

  @Selector()
  static refreshToken(state: AuthStateModel): string | null {
    return state.refreshToken;
  }

  @Selector()
  static sellerName(state: AuthStateModel): string | null {
    return state.sellerName;
  }

  @Selector()
  static grantedScopes(state: AuthStateModel): string[] {
    return state.grantedScopes;
  }

  @Selector()
  static error(state: AuthStateModel): any {
    return state.error;
  }

  @Action(GetAccessToken)
  getAccessToken(ctx: StateContext<AuthStateModel>, action: GetAccessToken) {
    return this.authService
      .getAccessToken(action.app_key, action.app_secret, action.auth_code)
      .pipe(
        tap(response => {
          ctx.dispatch(new GetAccessTokenSuccess(response.data));
        }),
        catchError(error => {
          ctx.dispatch(new GetAccessTokenFailure(error));
          return of(error);
        })
      );
  }

  @Action(GetAccessTokenSuccess)
  getAccessTokenSuccess(ctx: StateContext<AuthStateModel>, action: GetAccessTokenSuccess) {
    const { access_token, refresh_token, seller_name, granted_scopes } = action.payload;

    // Save the access token to localStorage
    this.localStorage.set(LocalStorageKeys.ACCESS_TOKEN, access_token);

    ctx.patchState({
      accessToken: access_token,
      refreshToken: refresh_token,
      sellerName: seller_name,
      grantedScopes: granted_scopes,
      error: null
    });
  }

  @Action(GetAccessTokenFailure)
  getAccessTokenFailure(ctx: StateContext<AuthStateModel>, action: GetAccessTokenFailure) {
    ctx.patchState({
      accessToken: null,
      refreshToken: null,
      sellerName: null,
      grantedScopes: [],
      error: action.error
    });
  }
}
